/**
 * QKey marketing shell — the root every redesigned page renders inside.
 *
 * PORTED FROM QSAVE (src/lib/site/SiteShell.svelte). Owns four things:
 *   1. the theme boundary ([data-qk-root]) that stops the app's theme showing
 *      through — see tokens.css;
 *   2. the skip link, which must be the FIRST child, before the nav;
 *   3. the grain overlay and hero glow (decorative, aria-hidden);
 *   4. nav + footer.
 *
 * tokens.css is imported HERE rather than in index.css so the fonts and the
 * theme boundary load only on marketing routes and never touch the rest of the
 * app. That isolation is the reason the port is safe to roll across every QKey
 * route without the wallet screens inheriting a marketing type scale.
 */
import type { ReactNode } from 'react'
import './tokens.css'
import { QkeySiteNav } from './QkeySiteNav'
import { QkeySiteFooter } from './QkeySiteFooter'

export function QkeySiteShell({ children }: { children: ReactNode }) {
  return (
    <div data-qk-root className="flex min-h-screen flex-col">
      <a data-qk-skip href="#main">
        Skip to content
      </a>

      {/* Grain. Purely decorative: aria-hidden, pointer-events:none, and it
          must never sit above interactive chrome (nav is z-8, this is z-9 but
          non-interactive by design). */}
      <svg
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[9] h-full w-full opacity-[0.05] mix-blend-overlay"
      >
        <filter id="qk-grain">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="3" stitchTiles="stitch" />
        </filter>
        <rect width="100%" height="100%" filter="url(#qk-grain)" />
      </svg>

      {/* Hero glow. A 900px circle bled off the top-left corner, fading to
          nothing at 62% of its radius. Sits below every section (they carry
          z-4) and below the nav (z-8), so it lifts the ground without touching
          content or interaction. It is the one piece of atmosphere on an
          otherwise deliberately flat surface. Indigo here where QSAVE's is
          green — see the colour-cast note in tokens.css. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-[200px] -top-[320px] h-[900px] w-[900px] rounded-full
                   bg-[radial-gradient(circle,theme(colors.qk.glow/0.16)_0%,theme(colors.qk.glow/0)_62%)]"
      />

      <QkeySiteNav />
      <div className="flex-1">{children}</div>
      <QkeySiteFooter />
    </div>
  )
}
